import { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useCaptchaChallenge } from '~/contexts/CaptchaChallengeContext'
import { FEATURE_FLAGS } from 'constants/feature-flags'
import { getIdToken } from 'helpers/auth'
import {
  CAPTCHA_OPERATIONS,
  CaptchaUnsolvedError,
} from 'helpers/captcha/contracts'
import type { CaptchaRequest } from 'helpers/captcha/contracts'
import { runWithCaptcha } from 'helpers/captcha/coordinator'
import { isQaPhoneNumber } from 'helpers/captcha/exemption'
import { getCaptchaSettings } from 'helpers/captcha/settings'
import { useFeatureFlags } from 'helpers/feature-flag'

const GUEST_OTP_CAPTCHA_ACTION = 'send_otp_phone'
const NON_DIGITS = /\D/g

/**
 * Sends one guest phone-OTP request under the CAPTCHA challenge.
 *
 * The Turnstile cData binds the token to what the backend checks it against:
 * `meta.phoneNumber` (digits only) for a typed number, or
 * `meta.invitationToken` as-is in the invitation flow, where the number is
 * only resolved server-side.
 */
export type GuestOtpRunner = <T>(
  request: CaptchaRequest<T>,
  meta: { phoneNumber?: string; invitationToken?: string },
) => Promise<T>

/**
 * Runs guest phone-OTP requests under the Turnstile challenge.
 *
 * A 403 CAPTCHA_REQUIRED opens the challenge and the request is resent with
 * the proof. Proactive solving, before the first request, needs the
 * `proactive-captcha-enabled` flag and a number that is not a staging QA
 * number. It applies to signed-in callers too: the endpoint challenges them
 * the same way.
 *
 * Each run supersedes the previous one: the earlier run's open challenge is
 * closed and it rejects as a cancellation. So does a run that outlives a
 * route change or unmount even though its request succeeded, and one whose id
 * token changes mid-flight, so a proof solved under one session is never sent
 * under another. `invalidate` cancels the current run without starting one.
 */
export default function useGuestOtpChallenge() {
  const router = useRouter()
  const { solveCaptcha, cancelCaptcha } = useCaptchaChallenge()
  const proactiveCaptchaEnabled = !!useFeatureFlags(
    FEATURE_FLAGS.proactiveCaptchaEnabled,
  )
  // Read at call time so `runGuestOtp` keeps a stable identity as the flag
  // flips.
  const proactiveEnabledRef = useRef(proactiveCaptchaEnabled)
  proactiveEnabledRef.current = proactiveCaptchaEnabled
  const generationRef = useRef(0)
  const mountedRef = useRef(false)

  const invalidate = useCallback(() => {
    generationRef.current += 1
    cancelCaptcha()
  }, [cancelCaptcha])

  const runGuestOtp = useCallback<GuestOtpRunner>(
    (request, { phoneNumber, invitationToken }) => {
      // Closes a superseded run's widget, which would otherwise stay open and
      // its promise pending whenever this run needs no challenge of its own.
      invalidate()
      const generation = generationRef.current
      const cData =
        invitationToken || phoneNumber?.replace(NON_DIGITS, '')
      const initialToken = getIdToken()

      return runWithCaptcha({
        operation: CAPTCHA_OPERATIONS.guestPhoneOtp,
        // Equality rather than emptiness, so a signed-in user's send still
        // goes out, but only under the session the proof was solved for.
        request: (...args) => {
          if (getIdToken() !== initialToken) {
            throw new CaptchaUnsolvedError({ outcome: 'cancelled' })
          }
          return request(...args)
        },
        solve: solveCaptcha,
        action: GUEST_OTP_CAPTCHA_ACTION,
        ...(cData ? { cData } : {}),
        settings: getCaptchaSettings(
          proactiveEnabledRef.current && !isQaPhoneNumber(phoneNumber),
        ),
        isCurrent: () =>
          mountedRef.current &&
          generationRef.current === generation &&
          getIdToken() === initialToken,
      })
    },
    [invalidate, solveCaptcha],
  )

  useEffect(() => {
    mountedRef.current = true
    router.events.on('routeChangeStart', invalidate)

    return () => {
      mountedRef.current = false
      router.events.off('routeChangeStart', invalidate)
      invalidate()
    }
  }, [invalidate, router.events])

  return { runGuestOtp, invalidate }
}
